import { useCallback } from 'react';

import { useTracking, CHAT_EVENTS, CHARACTER_EVENTS } from './useTracking';
import type { Database } from '../types/database';
import type { MessageMetadata } from '../lib/types';
import { MAX_UI_MESSAGES } from '../lib/constants';
import { useGemBalance } from './useGemBalance';

type Message = Database['eh']['Tables']['messages']['Row'];

const messageTrackedIds = new Set<string>();

/**
 * Hook for tracking chat-related events
 */
export function useChatTracking() {
  const { trackEvent } = useTracking();
  const { refreshBalance } = useGemBalance();

  /**
   * Track chat creation
   */
  const trackChatCreated = useCallback((chatId: string, characterId: string) => {
    trackEvent(CHAT_EVENTS.CHAT_CREATED, {
      chatId,
      characterId
    });
  }, [trackEvent]);

  /**
   * Track chat deletion
   */
  const trackChatDeleted = useCallback((chatId: string, characterId: string) => {
    trackEvent(CHAT_EVENTS.CHAT_DELETED, {
      chatId,
      characterId
    });
  }, [trackEvent]);

  /**
   * Track message sent
   */
  const trackMessageSent = useCallback(({ chatId, messageLength, messageType, characterId }: {
    chatId: string,
    messageLength: number,
    messageType: string,
    characterId: string
  }) => {
    trackEvent(CHAT_EVENTS.CHAT_MSG_SENT, {
      chatId,
      messageLength,
      messageType,
      characterId,
    });
  }, [trackEvent]);

  /**
   * Track text message received
   */
  const trackTextMessageReceived = useCallback(({ chatId, characterId, generationTimeMs, messageId }: {
    chatId: string,
    characterId: string,
    generationTimeMs?: number,
    messageId: string
  }) => {
    trackEvent(CHAT_EVENTS.CHAT_TEXT_MSG_RECEIVED, {
      chatId,
      characterId,
      generationTimeMs,
      messageId,
    });
  }, [trackEvent]);

  /**
   * Track image request
   */
  const trackImageRequested = useCallback(({ chatId, characterId, messageId }: {
    chatId: string,
    characterId: string,
    messageId?: string
  }) => {
    trackEvent(CHAT_EVENTS.CHAT_IMG_REQUESTED, {
      chatId,
      characterId,
      messageId
    });
  }, [trackEvent]);

  /**
   * Track image received
   */
  const trackImageReceived = useCallback(({ chatId, characterId, imageName, messageId, generationTimeMs }: {
    chatId: string,
    characterId: string,
    imageName?: string,
    messageId: string,
    generationTimeMs?: number,
  }) => {
    trackEvent(CHAT_EVENTS.CHAT_IMG_RECEIVED, {
      chatId,
      characterId,
      imageName,
      messageId,
      generationTimeMs
    });
    void refreshBalance();
  }, [refreshBalance, trackEvent]);

  /**
   * Track video received
   */
  const trackVideoReceived = useCallback(({ chatId, characterId, videoName, messageId }: {
    chatId: string,
    characterId: string,
    videoName?: string,
    messageId: string,
  }) => {
    trackEvent(CHAT_EVENTS.CHAT_VIDEO_RECEIVED, {
      chatId,
      characterId,
      videoName,
      messageId,
    });
    void refreshBalance();
  }, [refreshBalance, trackEvent]);

  /**
   * Track message received with deduplication and appropriate event based on message type
   */
  const trackMessageReceived = useCallback(({ chatId, characterId, message }: {
    chatId: string,
    characterId?: string | null,
    message: Message,
  }) => {
    if (messageTrackedIds.has(message.id)) {
      if (import.meta.env.DEV) console.log('Message already tracked:', message.id);
      return;
    }
    messageTrackedIds.add(message.id);
    if (messageTrackedIds.size > MAX_UI_MESSAGES) pruneTrackedIds();

    const metadata = message.msg_metadata as MessageMetadata;
    const startTimeStr = metadata?.image_requested_at;
    let generationTimeMs: number | undefined = undefined;
    
    if (startTimeStr) {
      const startTime = new Date(startTimeStr).getTime();
      const endTime = new Date(message.created_at).getTime();
      generationTimeMs = endTime - startTime;
    }

    if (message.msg_type === 'image' && message.msg_media_fpath) {
      trackImageReceived({
        chatId: chatId,
        characterId: characterId || 'unknown',
        imageName: message.msg_media_fpath.split('/').pop(),
        messageId: message.id,
        generationTimeMs,
      });
    } else if (message.msg_type === 'video' && message.msg_media_fpath) {
      trackVideoReceived({
        chatId: chatId,
        characterId: characterId || 'unknown',
        videoName: message.msg_media_fpath.split('/').pop(),
        messageId: message.id,
      });
    } else if (message.msg_type === 'text') {
      trackTextMessageReceived({
        chatId: chatId,
        characterId: characterId || 'unknown',
        messageId: message.id,
        generationTimeMs
      });
    }
  }, [trackImageReceived, trackVideoReceived, trackTextMessageReceived]);

  /**
   * Track chat error
   */
  const trackChatError = useCallback(({ chatId, characterId, error }: {
    chatId: string,
    characterId: string,
    error: unknown
  }) => {
    trackEvent(CHAT_EVENTS.CHAT_ERROR, {
      chatId,
      characterId,
      error: error instanceof Error ? error.message : String(error)
    });
  }, [trackEvent]);

  /**
   * Track character generation from image
   */
  const trackCharacterFromImageGenerated = useCallback(({ characterId, imageUrl, success, error }: {
    characterId: string,
    imageUrl: string,
    success: boolean,
    error?: string
  }) => {
    trackEvent(CHARACTER_EVENTS.CHARACTER_FROM_IMG_GENERATED, {
      characterId,
      imageUrl,
      success,
      error
    });
  }, [trackEvent]);

  return {
    trackChatCreated,
    trackChatDeleted,
    trackMessageReceived,
    trackMessageSent,
    trackImageRequested,
    trackChatError,
    trackCharacterFromImageGenerated
  };
}

function pruneTrackedIds() {
  const size = messageTrackedIds.size;
  if (import.meta.env.DEV) console.log('[DEV] Pruning messageTrackedIds, size:', size);
  let i = 0;
  for (const id of messageTrackedIds) {
    messageTrackedIds.delete(id);
    if (i++ > Math.ceil(size / 10)) break;
  }
} 